import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  User, Smartphone, Save,
  MessageCircle, Settings
} from 'lucide-react';
import axios from 'axios';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 20px;`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
`;
const Title = styled.h1`font-size: 22px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;

const ProfileCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  padding: 24px; display: flex; align-items: center; gap: 20px;
`;

const Avatar = styled.div`
  width: 64px; height: 64px; border-radius: 50%;
  background: linear-gradient(135deg, #1da1f2, #000);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 24px; font-weight: 700; flex-shrink: 0;
`;

const ProfileMeta = styled.div`
  flex: 1;
  h2 { font-size: 18px; font-weight: 700; color: var(--text-primary); margin: 0 0 6px; }
`;

const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
`;

const StatBox = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 10px;
  padding: 14px; text-align: center;
  .value { font-size: 20px; font-weight: 800; color: var(--text-primary); }
  .label { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
`;

const Section = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  padding: 20px;
  h3 { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0 0 14px; display: flex; align-items: center; gap: 8px; }
`;

const FormRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 4px; }
  input, select { width: 100%; padding: 8px 12px; border-radius: 8px; border: 1px solid var(--card-border);
    background: var(--bg); color: var(--text-primary); font-size: 13px; outline: none;
    &:focus { border-color: var(--primary); }
  }
`;

const Btn = styled.button`
  padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
  border: none; display: flex; align-items: center; gap: 6px;
  background: ${p => p.$primary ? 'var(--primary)' : 'var(--card-bg)'};
  color: ${p => p.$primary ? '#fff' : 'var(--text-primary)'};
  border: 1px solid ${p => p.$primary ? 'transparent' : 'var(--card-border)'};
  &:hover { opacity: .85; }
`;

const Badge = styled.span`
  padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 700;
  background: rgba(29,161,242,.12); color: #1da1f2;
`;

const NoDevice = styled.div`
  text-align: center; padding: 60px 20px; color: var(--text-muted);
  svg { margin-bottom: 12px; opacity: .4; }
  h3 { font-size: 16px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
`;

/* ═══════════════════════════════════════════════ */

const TwitterAccount = ({ selectedDevice }) => {
  const [, setAccount] = useState(null);
  const [form, setForm] = useState({
    username: '', display_name: '', account_type: 'OUR', niche: '', proxy_url: '',
    speed_level: 2, daily_follow_limit: 400, daily_like_limit: 1000,
    daily_retweet_limit: 200, daily_dm_limit: 50,
    session_max_follows: 30, session_max_likes: 100,
    rest_minutes_min: 5, rest_minutes_max: 15
  });
  const [saving, setSaving] = useState(false);

  const deviceId = selectedDevice?.device_id;

  useEffect(() => {
    if (!deviceId) return;
    axios.get(`/api/twitter-scripts/account/${deviceId}`)
      .then(r => {
        if (r.data.account) {
          setAccount(r.data.account);
          setForm(prev => ({ ...prev, ...r.data.account }));
        }
      })
      .catch(() => {});
  }, [deviceId]);

  const handleSave = async () => {
    if (!deviceId || !form.username) return;
    setSaving(true);
    try {
      await axios.put(`/api/twitter-scripts/account/${deviceId}`, form);
      setAccount(form);
    } catch (e) {
      console.error('Save error:', e);
    }
    setSaving(false);
  };

  if (!deviceId) {
    return (
      <Page>
        <NoDevice>
          <Smartphone size={48} />
          <h3>Aucun appareil sélectionné</h3>
          <p>Sélectionnez un appareil dans la sidebar pour configurer son compte Twitter/X.</p>
        </NoDevice>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <Title><MessageCircle size={24} color="#1da1f2" /> Compte Twitter/X</Title>
        <Badge>Bêta</Badge>
      </Header>

      <ProfileCard>
        <Avatar>{form.username ? form.username[0].toUpperCase() : <User size={28} />}</Avatar>
        <ProfileMeta>
          <h2>{form.username ? `@${form.username}` : 'Non configuré'}</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {selectedDevice.device_name || deviceId} • {form.account_type || 'OUR'}
          </div>
        </ProfileMeta>
        <Btn $primary onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Sauvegarder'}
        </Btn>
      </ProfileCard>

      <StatsRow>
        <StatBox><div className="value">0</div><div className="label">Likes</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">Retweets</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">Follows</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">DMs</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">Sessions</div></StatBox>
      </StatsRow>

      <Section>
        <h3><User size={16} color="#1da1f2" /> Informations du compte</h3>
        <FormRow>
          <Field>
            <label>Nom d'utilisateur (@handle)</label>
            <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="username" />
          </Field>
          <Field>
            <label>Nom affiché</label>
            <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Display Name" />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Type de compte</label>
            <select value={form.account_type} onChange={e => setForm(f => ({ ...f, account_type: e.target.value }))}>
              <option value="OUR">Notre compte</option>
              <option value="CLIENT">Compte client</option>
            </select>
          </Field>
          <Field>
            <label>Niche</label>
            <input value={form.niche} onChange={e => setForm(f => ({ ...f, niche: e.target.value }))} placeholder="ex: tech, fitness..." />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Proxy URL</label>
            <input value={form.proxy_url} onChange={e => setForm(f => ({ ...f, proxy_url: e.target.value }))} placeholder="socks5://user:pass@host:port" />
          </Field>
        </FormRow>
      </Section>

      <Section>
        <h3><Settings size={16} color="#1da1f2" /> Limites d'automatisation</h3>
        <FormRow>
          <Field>
            <label>Vitesse (1-5)</label>
            <select value={form.speed_level} onChange={e => setForm(f => ({ ...f, speed_level: parseInt(e.target.value) }))}>
              {[1,2,3,4,5].map(v => <option key={v} value={v}>{v} — {['Très lent','Lent','Normal','Rapide','Très rapide'][v-1]}</option>)}
            </select>
          </Field>
          <Field>
            <label>Repos entre sessions (min)</label>
            <input type="number" value={form.rest_minutes_min} onChange={e => setForm(f => ({ ...f, rest_minutes_min: parseInt(e.target.value) || 5 }))} />
          </Field>
        </FormRow>
        <FormRow>
          <Field><label>Likes / jour</label><input type="number" value={form.daily_like_limit} onChange={e => setForm(f => ({ ...f, daily_like_limit: parseInt(e.target.value) || 0 }))} /></Field>
          <Field><label>Follows / jour</label><input type="number" value={form.daily_follow_limit} onChange={e => setForm(f => ({ ...f, daily_follow_limit: parseInt(e.target.value) || 0 }))} /></Field>
        </FormRow>
        <FormRow>
          <Field><label>Retweets / jour</label><input type="number" value={form.daily_retweet_limit} onChange={e => setForm(f => ({ ...f, daily_retweet_limit: parseInt(e.target.value) || 0 }))} /></Field>
          <Field><label>DMs / jour</label><input type="number" value={form.daily_dm_limit} onChange={e => setForm(f => ({ ...f, daily_dm_limit: parseInt(e.target.value) || 0 }))} /></Field>
        </FormRow>
        <FormRow>
          <Field><label>Max follows / session</label><input type="number" value={form.session_max_follows} onChange={e => setForm(f => ({ ...f, session_max_follows: parseInt(e.target.value) || 0 }))} /></Field>
          <Field><label>Max likes / session</label><input type="number" value={form.session_max_likes} onChange={e => setForm(f => ({ ...f, session_max_likes: parseInt(e.target.value) || 0 }))} /></Field>
        </FormRow>
      </Section>
    </Page>
  );
};

export default TwitterAccount;
